<template>
  <div
    id="app"
    @keyup="keyup"
    @keyup.space="space"
    tabindex="-1"
    :class="[
      {
        night: grimoire.isNight,
        'sheet-open': isDesktopSheets && isSheetOpen,
        static: grimoire.isStatic,
      },
      'locale-' + locale,
    ]"
    :style="{
      backgroundImage: grimoire.background
        ? `url('${grimoire.background}')`
        : edition.background && grimoire.isImageOptIn
          ? `url('${edition.background}')`
          : '',
    }"
  >
    <main class="app-main">
      <video
        id="background"
        v-if="
          grimoire.background && grimoire.background.match(/\.(mp4|webm)$/i)
        "
        :src="grimoire.background"
        autoplay
        loop
      ></video>
      <div class="backdrop"></div>
      <GameTimer />
      <transition name="blur">
        <Intro v-if="!players.length"></Intro>
        <TownInfo v-if="players.length && !session.nomination"></TownInfo>
        <Vote v-if="session.nomination" ref="vote"></Vote>
      </transition>
      <TownSquare :is-side-panel-open="isDesktopSheets && isSheetOpen" />
      <Menu
        ref="menu"
        :desktop-sheets="isDesktopSheets"
        @toggle-sheet="toggleDesktopSheet"
      ></Menu>
      <EditionModal />
      <NpcModal />
      <RolesModal />
      <ReferenceModal v-if="!isDesktopSheets" presentation="modal" />
      <NightOrderModal v-if="!isDesktopSheets" presentation="modal" />
      <VoteHistoryModal />
      <GameStateModal />
      <TimerModal />
      <DayTrackerModal />
      <Gradients />
      <span id="version">v{{ version }}</span>
    </main>
    <aside
      class="sheets-rail"
      v-if="isDesktopSheets && isSheetOpen"
      :style="sheetRailStyle"
    >
      <div class="sheets-rail-controls">
        <button
          class="sheets-zoom-button"
          type="button"
          :title="$t('menu.zoom') + ' -'"
          :aria-label="$t('menu.zoom') + ' -'"
          :disabled="sheetZoom <= sheetZoomMin"
          @click="adjustSheetZoom(-sheetZoomStep)"
        >
          <font-awesome-icon icon="search-minus" />
        </button>
        <button
          class="sheets-zoom-button"
          type="button"
          :title="$t('menu.zoom') + ' +'"
          :aria-label="$t('menu.zoom') + ' +'"
          :disabled="sheetZoom >= sheetZoomMax"
          @click="adjustSheetZoom(sheetZoomStep)"
        >
          <font-awesome-icon icon="search-plus" />
        </button>
      </div>
      <ReferenceModal
        v-if="desktopSheet === 'reference'"
        presentation="panel"
        force-visible
        @show-sheet="showDesktopSheet"
        @close-sheet="closeDesktopSheet"
      />
      <NightOrderModal
        v-else-if="desktopSheet === 'nightOrder'"
        presentation="panel"
        force-visible
        @show-sheet="showDesktopSheet"
        @close-sheet="closeDesktopSheet"
      />
    </aside>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { version } from "../package.json";
import TownSquare from "./components/TownSquare";
import TownInfo from "./components/TownInfo";
import Menu from "./components/Menu";
import RolesModal from "./components/modals/RolesModal";
import EditionModal from "./components/modals/EditionModal";
import Intro from "./components/Intro";
import ReferenceModal from "./components/modals/ReferenceModal";
import Vote from "./components/Vote";
import Gradients from "./components/Gradients";
import NightOrderModal from "./components/modals/NightOrderModal";
import NpcModal from "@/components/modals/NpcModal";
import VoteHistoryModal from "@/components/modals/VoteHistoryModal";
import GameStateModal from "@/components/modals/GameStateModal";
import GameTimer from "@/components/GameTimer";
import TimerModal from "@/components/modals/TimerModal";
import DayTrackerModal from "@/components/modals/DayTrackerModal";

export default {
  components: {
    DayTrackerModal,
    TimerModal,
    GameTimer,
    GameStateModal,
    VoteHistoryModal,
    NpcModal,
    NightOrderModal,
    Vote,
    ReferenceModal,
    Intro,
    TownInfo,
    TownSquare,
    Menu,
    EditionModal,
    RolesModal,
    Gradients,
  },
  computed: {
    isSheetOpen: function () {
      return Boolean(this.roles.size && this.desktopSheet);
    },
    sheetRailStyle: function () {
      const zoom = this.sheetZoom;
      return {
        "--sheet-ref-card-basis": `${240 * zoom}px`,
        "--sheet-ref-icon-size": `${58 * zoom}px`,
        "--sheet-ref-title-size": `${18 * zoom}px`,
        "--sheet-night-headline-size": `${16 * zoom}px`,
        "--sheet-night-icon-size": `${42 * zoom}px`,
        "--sheet-night-text-offset": `${48 * zoom}px`,
        "--sheet-night-title-size": `${18 * zoom}px`,
        fontSize: `${100 * zoom}%`,
      };
    },
    ...mapState([
      "edition",
      "grimoire",
      "session",
      "locale",
      "modals",
      "roles",
    ]),
    ...mapState("players", ["players"]),
  },
  data() {
    return {
      desktopSheet: null,
      isDesktopSheets: false,
      sheetZoom: 1.2,
      sheetZoomMax: 2.5,
      sheetZoomMin: 0.75,
      sheetZoomStep: 0.1,
      version,
    };
  },
  mounted() {
    this.updateSheetLayout();
    window.addEventListener("resize", this.updateSheetLayout);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateSheetLayout);
  },
  methods: {
    updateSheetLayout() {
      const wasDesktopSheets = this.isDesktopSheets;
      this.isDesktopSheets = window.matchMedia(
        "(min-width: 1100px) and (orientation: landscape)",
      ).matches;
      if (this.isDesktopSheets && !wasDesktopSheets) {
        //this.openDefaultDesktopSheet();
      }
    },
    openDefaultDesktopSheet() {
      if (!this.isDesktopSheets || !this.roles.size || this.desktopSheet) {
        return;
      }
      this.desktopSheet = "reference";
    },
    toggleDesktopSheet(sheet) {
      this.desktopSheet = this.desktopSheet === sheet ? null : sheet;
    },
    showDesktopSheet(sheet) {
      this.desktopSheet = sheet;
    },
    closeDesktopSheet() {
      this.desktopSheet = null;
    },
    adjustSheetZoom(delta) {
      const nextZoom = Math.round((this.sheetZoom + delta) * 100) / 100;
      this.sheetZoom = Math.min(
        this.sheetZoomMax,
        Math.max(this.sheetZoomMin, nextZoom),
      );
    },
    keyup({ code, ctrlKey, metaKey }) {
      if (ctrlKey || metaKey) return;
      switch (code) {
        case "KeyG":
          this.$store.commit("toggleGrimoire");
          break;
        case "KeyA":
          this.$refs.menu.addPlayer();
          break;
        case "KeyH":
          this.$refs.menu.hostSession();
          break;
        case "KeyJ":
          this.$refs.menu.joinSession();
          break;
        case "KeyR":
          if (this.isDesktopSheets) {
            this.toggleDesktopSheet("reference");
          } else {
            this.$store.commit("toggleModal", "reference");
          }
          break;
        case "KeyN":
          if (this.isDesktopSheets) {
            this.toggleDesktopSheet("nightOrder");
          } else {
            this.$store.commit("toggleModal", "nightOrder");
          }
          break;
        case "KeyE":
          if (this.session.isSpectator) return;
          this.$store.commit("toggleModal", "edition");
          break;
        case "KeyC":
          if (this.session.isSpectator) return;
          this.$store.commit("toggleModal", "roles");
          break;
        case "KeyV":
          if (this.session.voteHistory.length || !this.session.isSpectator) {
            this.$store.commit("toggleModal", "voteHistory");
          }
          break;
        case "KeyS":
          if (this.session.isSpectator) return;
          this.$refs.menu.toggleNight();
          break;
        case "KeyT":
          if (this.session.isSpectator) return;
          this.$store.commit("toggleModal", "timer");
          break;
        case "Escape":
          if (this.isDesktopSheets) {
            this.toggleDesktopSheet("");
          } else {
            this.$store.commit("toggleModal", "reference");
          }
          break;
        case "ArrowUp":
          if (this.session.nomination) {
            this.$refs.vote.vote(true);
          }
          break;
        case "ArrowDown":
          if (this.session.nomination) {
            this.$refs.vote.vote(false);
          }
          break;
      }
    },
    space() {
      const player = this.players.find((p) => p.id === this.session.playerId);
      if (
        !player ||
        (this.session.isSpectator && player.id !== this.session.playerId)
      )
        return;
      this.$store.commit("players/update", {
        player,
        property: "handRaised",
        value: !player.handRaised,
      });
    },
  },
};
</script>

<style lang="scss">
@import "vars";

@font-face {
  font-family: "Papyrus";
  src: url("assets/fonts/papyrus.eot"); /* IE9*/
  src:
    url("assets/fonts/papyrus.eot?#iefix") format("embedded-opentype"),
    /* IE6-IE8 */ url("assets/fonts/papyrus.woff2") format("woff2"),
    /* chrome firefox */ url("assets/fonts/papyrus.woff") format("woff"),
    /* chrome firefox */ url("assets/fonts/papyrus.ttf") format("truetype"),
    /* chrome firefox opera Safari, Android, iOS 4.2+*/
      url("assets/fonts/papyrus.svg#PapyrusW01") format("svg"); /* iOS 4.1- */
}

@font-face {
  font-family: PiratesBay;
  src: url("assets/fonts/piratesbay.ttf");
  font-display: swap;
}

html,
body {
  font-size: 1.2em;
  line-height: 1.4;
  background: url("assets/background.webp") center center;
  background-size: cover;
  color: white;
  height: 100%;
  font-family: "Roboto Condensed", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  padding: 0;
  margin: 0;
  overflow: hidden;
}

@import "media";

* {
  box-sizing: border-box;
  position: relative;
  user-select: none;
}

a {
  color: $townsfolk;
  &:hover {
    color: $demon;
  }
}

h1,
h2,
h3,
h4,
h5 {
  margin: 0;
  text-align: center;
  font-family: PiratesBay, sans-serif;
  letter-spacing: 1px;
  font-weight: normal;
}

ul {
  list-style-type: none;
  margin: 0;
  padding: 0;
}

#app {
  height: 100%;
  background-position: center center;
  background-size: cover;
  display: flex;
  align-items: stretch;
  align-content: stretch;
  justify-content: stretch;
  overflow: hidden;

  // disable all animations
  &.static *,
  &.static *:after,
  &.static *:before {
    transition: none !important;
    animation: none !important;
  }
}

.app-main {
  min-width: 0;
  height: 100%;
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
  overflow: hidden;
}

.sheets-rail {
  z-index: 80;
  width: clamp(620px, 48vw, 920px);
  max-width: 62vw;
  height: 100%;
  flex: 0 0 clamp(620px, 48vw, 920px);
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.62);
  border-left: 3px solid rgba(255, 255, 255, 0.18);
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.65);
}

.sheets-rail-controls {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 2px;
}

.sheets-zoom-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  color: white;
  background: rgba(0, 0, 0, 0.62);
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 6px;
  cursor: pointer;
  filter: drop-shadow(0 0 5px rgba(0, 0, 0, 0.9));

  svg {
    width: 72%;
    height: 72%;
  }

  &:hover:not(:disabled) {
    color: #ffd15c;
    border-color: rgba(255, 209, 92, 0.72);
  }

  &:disabled {
    cursor: default;
    opacity: 0.35;
  }
}

@media screen and (max-width: 1099.98px), (orientation: portrait) {
  #app {
    flex-direction: column;
  }

  .app-main {
    flex: 1 1 50%;
    min-height: 0;
  }

  .sheets-rail {
    width: 100%;
    max-width: 100%;
    height: auto;
    flex: 1 1 50%;
    min-height: 0;
    border-top: 3px solid rgba(255, 255, 255, 0.18);
    border-left: 0;
  }
}

#version {
  position: absolute;
  text-align: right;
  right: 10px;
  bottom: 10px;
  font-size: 60%;
  opacity: 0.5;
}

.blur-enter-active,
.blur-leave-active {
  transition: all 250ms;
  filter: blur(0);
}
.blur-enter,
.blur-leave-to {
  opacity: 0;
  filter: blur(20px);
}

// Buttons
.button-group {
  display: flex;
  align-items: center;
  justify-content: center;
  align-content: center;
  .button {
    margin: 5px 0;
    border-radius: 0;
    &:first-child {
      border-top-left-radius: 15px;
      border-bottom-left-radius: 15px;
    }
    &:last-child {
      border-top-right-radius: 15px;
      border-bottom-right-radius: 15px;
    }
  }
}
.button {
  padding: 0;
  border: solid 0.125em transparent;
  border-radius: 15px;
  box-shadow:
    inset 0 1px 1px #9c9c9c,
    0 0 10px #000;
  background:
    radial-gradient(at 0 -15%, rgba(#fff, 0.07) 70%, rgba(#fff, 0) 71%) 0 0/ 80%
      90% no-repeat content-box,
    linear-gradient(#4e4e4e, #040404) content-box,
    linear-gradient(#292929, #010101) border-box;
  color: white;
  font-weight: bold;
  text-shadow: 1px 1px rgba(0, 0, 0, 0.5);
  line-height: 170%;
  margin: 5px auto;
  cursor: pointer;
  transition: all 200ms;
  white-space: nowrap;
  &:hover {
    color: red;
  }
  &.disabled {
    color: gray;
    cursor: default;
    opacity: 0.75;
  }
  &:before,
  &:after {
    content: " ";
    display: inline-block;
    width: 10px;
    height: 10px;
  }
  &.townsfolk {
    background:
      radial-gradient(
          at 0 -15%,
          rgba(255, 255, 255, 0.07) 70%,
          rgba(255, 255, 255, 0) 71%
        )
        0 0/80% 90% no-repeat content-box,
      linear-gradient(#0031ad, rgba(5, 0, 0, 0.22)) content-box,
      linear-gradient(#292929, #001142) border-box;
    box-shadow:
      inset 0 1px 1px #002c9c,
      0 0 10px #000;
    &:hover:not(.disabled) {
      color: #008cf7;
    }
  }
  &.demon {
    background:
      radial-gradient(
          at 0 -15%,
          rgba(255, 255, 255, 0.07) 70%,
          rgba(255, 255, 255, 0) 71%
        )
        0 0/80% 90% no-repeat content-box,
      linear-gradient(#ad0000, rgba(5, 0, 0, 0.22)) content-box,
      linear-gradient(#292929, #420000) border-box;
    box-shadow:
      inset 0 1px 1px #9c0000,
      0 0 10px #000;
  }
}

/* video background */
video#background {
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Night phase backdrop */
.app-main > .backdrop {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  pointer-events: none;
  background: black;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 1) 0%,
    rgba(1, 22, 46, 1) 50%,
    rgba(0, 39, 70, 1) 100%
  );
  opacity: 0;
  transition: opacity 1s ease-in-out;
  &:after {
    content: " ";
    display: block;
    width: 100%;
    padding-right: 2000px;
    height: 100%;
    background: url("assets/clouds.webp") repeat;
    background-size: 2000px auto;
    animation: move-background 120s linear infinite;
    opacity: 0.3;
  }
}

@keyframes move-background {
  from {
    transform: translate3d(-2000px, 0px, 0px);
  }
  to {
    transform: translate3d(0px, 0px, 0px);
  }
}

#app.night .app-main > .backdrop {
  opacity: 0.5;
}
</style>
